import {useSnackbar} from 'notistack';
import React, {useCallback, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import Loading from '../../components/Loading/Loading';

import {useAccountContext, useAccountFull} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useQueryParams} from '../../hooks/useQueryParams';
import {ContactAttribution} from '../PrecursorSummaryPage/headerFields';

import withAutoFormNew, {AutoFormProps} from '../../components/AutoForm/AutoFormNew';
import {WizardField} from '../../components/Wizard/QuestionWrapper';
import {Wizard} from '../../components/Wizard/Wizard';
import {getContactFieldValues} from './getContactFieldValues';
import useUpdatePostcursor, {
  getPostcursorFields,
  postcursorFieldsType,
} from './postcursorFields';
import {PostcursorWizardSummary} from './PostcursorWizardSummary';
import {useAccountVulnerabilities} from '../../components/AccountOverview/accountOverviewQueries';
import {ErrorPage} from '../ErrorPage';
import {useRTQ} from '../../hooks/useRTQ';

export function PostcursorWizardPage() {
  return (
    <>
      <HandoverStep />
      <PostcursorWizard />
    </>
  );
}
export function PostcursorWizard() {
  const {reviewId} = useParams<{
    reviewId: string;
  }>();
  const params = useQueryParams();
  const {account} = useAccountContext();
  const {accountFull} = useAccountFull();
  const {vulnerabilities} = useAccountVulnerabilities();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {canUseRisk} = useRTQ();
  const {reviewDetails} = useReviewContext();
  const skipPostcursorInitialOverrides = useMemo(() => {
    if (params.get('skip') === 'true') {
      const initialValue: Partial<postcursorFieldsType> = {
        reviewContact: ContactAttribution.both,
      };
      return initialValue;
    }
    return undefined;
  }, [params, account?.secondaryContact]);
  const onError = useCallback(
    () =>
      enqueueSnackbar('There was a problem with your answers. Please try again', {
        variant: 'error',
        autoHideDuration: null,
      }),
    [enqueueSnackbar]
  );

  const {handleUpdatePostcursor} = useUpdatePostcursor({
    onSuccess: () => {
      navigate({
        pathname: `/account/${account?.id}/review-journey/${reviewId}/resilience`,
      });
    },
    onError,
  });

  const fields = useMemo(() => {
    if (reviewDetails && accountFull) {
      return getPostcursorFields(reviewDetails, accountFull, canUseRisk);
    }
    return null;
  }, [reviewDetails, accountFull]);

  if (!accountFull) {
    return <Loading entityName="account" />;
  }

  if (!reviewId || !account || !vulnerabilities) {
    return <Loading entityName="review" />;
  }

  if (!fields || fields.length === 0) {
    return <ErrorPage text="No fields found" />;
  }

  if (!reviewDetails) {
    return <ErrorPage code="404" text="Review not found" />;
  }

  const contactFields = getContactFieldValues(vulnerabilities);

  const initialValueOverrides: Partial<postcursorFieldsType> = {
    reviewContact: reviewDetails?.contact
      ? reviewDetails?.contact.id
      : ContactAttribution.both,
    step: 0,
    opportunityValue: reviewDetails?.opportunityValue,
    ...contactFields,
  };

  return (
    <PostcursorFormWrapper
      initialValueOverrides={skipPostcursorInitialOverrides || initialValueOverrides}
      submit={(values) => {
        return handleUpdatePostcursor(
          {
            ...values,
          },
          canUseRisk
        );
      }}
      validationSchema={undefined}
      formFields={fields}
    />
  );
}

const PostcursorForm: React.FC<
  AutoFormProps<postcursorFieldsType, WizardField<postcursorFieldsType>>
> = ({formFields}) => {
  return (
    <Wizard
      goTo="meeting"
      formFields={formFields}
      SummaryComponent={PostcursorWizardSummary}
    />
  );
};

const PostcursorFormWrapper = withAutoFormNew<
  postcursorFieldsType,
  unknown,
  WizardField<postcursorFieldsType>
>(PostcursorForm);
